<script setup lang="ts">
/**
 * Layout de las secciones del perfil de un dive center.
 *
 * Antes esto era una sola página con cinco tabs. Las tabs no dejaban enlace
 * propio ni respuesta al botón atrás, así que cada sección pasó a ser una ruta
 * hija (`profile/`) y el menú lateral las lista. Acá queda lo que es común a
 * todas: la carga del centro, la cabecera y el bloque de publicación.
 *
 * `meta.requiresAuth` no se repite en las hijas: `to.meta` es la fusión de
 * todos los registros matcheados, así que el guard ya lo ve desde acá.
 */
import { ApiError } from '@/api/client'
import { getDiveCenter, getGooglePlaceDetails } from '@/api/dive-centers'
import { provideDiveCenterProfile } from '@/composables/useDiveCenterProfile'
import type { DiveCenter, GooglePlaceDetails } from '@/types'

definePage({ meta: { requiresAuth: true } })

const route = useRoute('dive-centers-id-profile')
const router = useRouter()

const id = String(route.params.id)
const diveCenter = ref<DiveCenter | null>(null)
const loading = ref(true)
const loadError = ref('')


// Rating de Google en vivo (nunca se persiste).
const gDetails = ref<GooglePlaceDetails | null>(null)

watch(
  () => diveCenter.value?.googlePlaceId,
  async placeId => {
    gDetails.value = null
    if (!placeId)
      return

    try {
      gDetails.value = await getGooglePlaceDetails(id)
    }
    catch {
      gDetails.value = null
    }
  },
)

const applyDiveCenter = (dc: DiveCenter) => {
  diveCenter.value = dc
}

const load = async () => {
  loading.value = true
  loadError.value = ''
  try {
    const res = await getDiveCenter(id)

    applyDiveCenter(res.diveCenter)
  }
  catch (e) {
    loadError.value = e instanceof ApiError ? e.message : 'Could not load dive center.'
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

provideDiveCenterProfile({ diveCenter, applyDiveCenter, gDetails, id })

/*
 * Compatibilidad con los `?tab=` de cuando esto era una sola página con tabs:
 * pueden estar en un favorito o en un enlace viejo. Se traducen a su ruta y se
 * limpia la query.
 */
const TAB_ROUTES: Record<string, string> = {
  basic: 'dive-centers-id-profile-edit',
}

onMounted(() => {
  const legacy = String(route.query.tab ?? '')
  const name = TAB_ROUTES[legacy]

  if (name) {
    router.replace({ name, params: { id } } as never)

    return
  }

  /*
   * `/profile` a secas ya no tiene contenido: el Overview del perfil se fusionó
   * con el del centro. Se manda a la primera sección editable.
   */
  if (route.path.endsWith('/profile'))
    router.replace({ name: 'dive-centers-id-profile-edit', params: { id } })
})


</script>

<template>
  <div class="mx-auto" style="max-width: 1100px;">
    <!-- Sin enlace de vuelta: las secciones del perfil y el Overview cuelgan
         del mismo grupo del menú lateral. -->
    <div
      v-if="loading"
      class="d-flex justify-center py-16"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </div>

    <VAlert
      v-else-if="loadError"
      type="error"
      variant="tonal"
      density="compact"
    >
      {{ loadError }}
    </VAlert>

    <template v-else-if="diveCenter">
      <!-- La sección activa. Se monta recién con el centro cargado, así las
           hijas no tienen que defenderse de un `diveCenter` nulo. -->
      <RouterView />
    </template>

  </div>
</template>
